import { api, DATA_BASE } from '../../services/apiClient';

export interface EventRow {
  event_id: number; kydemy_id: number | null; name: string; description: string | null;
  datetime: string | null; duration: number | null; price: string | null; capacity: number | null;
  venue: string | null; venue_address: string | null; image_url: string | null;
  event_type: string | null; event_type_color: string | null;
  must_rsvp: number; observations: string | null; active: number; cancelled: number; source: string;
  going?: number;
  // Finanzas del festival (26-oct-2026). Opcionales: la API vieja no los manda.
  fin_mode?: 'completo' | 'utilidad'; is_reference?: number; fin_concept_id?: number | null; profit_payment_id?: number | null;
  budget_profit?: string | number | null; budget_lines?: number;
  // Control de boletos del festival (28-oct-2026): 'simple' = asistentes de siempre · 'festival' = participantes, ventas con folio, caja.
  ticket_mode?: 'simple' | 'festival';
  // Acceso por evento (28-oct-2026, Mabel): null = todo el equipo; lista = solo esas personas (los dueños siempre).
  // equipo_vende = quien tiene acceso puede vender/entregar boletos y abrir la caja aunque su rol solo «vea».
  access_user_ids?: number[] | null; equipo_vende?: number;
  acceso?: { puede_operar: boolean };
}
export interface Attendee {
  account_id: number; going: number | null; qty: number; status: string | null;
  phase_id: number | null; phase_name: string | null; source: string;
  firstname: string; lastname: string; has_photo: number; phone: string | null;
}
export interface EventPhase {
  phase_id: number; name: string; starts_at: string | null; ends_at: string | null;
  capacity: number | null; max_per_account: number | null; price: string | null;
  sort: number; active: number; sold: number | string;
  // Banderas del modo festival (0/1): F1 «solo participantes», F2 «solo quien dejó lista de espera».
  solo_participantes?: number | boolean; solo_lista_espera?: number | boolean;
}
export interface PhaseInput {
  name: string; starts_at?: string | null; ends_at?: string | null;
  capacity?: number | string | null; max_per_account?: number | string | null;
  price?: number | string | null; sort?: number; active?: number;
  solo_participantes?: number; solo_lista_espera?: number;
}
export interface EventDetail {
  data: EventRow;
  phases: EventPhase[];
  attendees: Attendee[];
  tickets: { going: number; waitlist: number };
  revenue: { count: number; total: number };
}
export interface EventInput {
  name: string; datetime?: string | null; price?: number | string | null; capacity?: number | string | null;
  venue?: string | null; venue_address?: string | null; event_type?: string | null;
  description?: string | null; image_url?: string | null;
  access_user_ids?: number[] | null; equipo_vende?: 0 | 1;
}

export interface EventHomeSummary {
  event_id: number; name: string; datetime: string | null;
  price: string | null; capacity: number | null; venue: string | null;
  event_type: string | null; event_type_color: string | null; image_url: string | null;
  days_until: number; going: number;
  /** #1592: vendidos (festival = ventas del boletaje; simple = confirmados) y disponibles (null sin cupo). */
  ticket_mode?: string | null; sold?: number; available?: number | null;
}
export function getEventHomeSummary() {
  return api.get<{ data: EventHomeSummary | null; revenue?: { count: number; total: number } }>('/events/home-summary');
}

export function listEvents() { return api.get<{ data: EventRow[] }>('/events'); }
export function getEvent(id: number) { return api.get<EventDetail>(`/events/${id}`); }
export function createEvent(e: EventInput) { return api.post<{ ok: true; event_id: number }>('/events', e); }
export function updateEvent(id: number, e: EventInput) { return api.put<{ ok: true }>(`/events/${id}`, e); }
export function addAttendee(eventId: number, account_id: number, qty = 1, phase_id?: number | null) {
  return api.post<{ ok: true; status: string; qty: number }>(`/events/${eventId}/attendees`, { account_id, qty, phase_id: phase_id ?? null });
}
export function setAttendeeStatus(eventId: number, accountId: number, status: 'going' | 'waitlist') {
  return api.patch<{ ok: true; status: string }>(`/events/${eventId}/attendees/${accountId}`, { status });
}
export function removeAttendee(eventId: number, accountId: number) {
  return api.del<{ ok: true }>(`/events/${eventId}/attendees/${accountId}`);
}

// ── Fases de boletaje ──
export function createPhase(eventId: number, p: PhaseInput) {
  return api.post<{ ok: true; phase_id: number }>(`/events/${eventId}/phases`, p);
}
export function updatePhase(eventId: number, phaseId: number, p: PhaseInput) {
  return api.put<{ ok: true }>(`/events/${eventId}/phases/${phaseId}`, p);
}
export function deletePhase(eventId: number, phaseId: number) {
  return api.del<{ ok: true }>(`/events/${eventId}/phases/${phaseId}`);
}

// ── Finanzas del evento ──
export interface EventIncome {
  payment_id: number; account_id: number | null; datetime: string | null;
  amount: string; method: string | null; comment: string | null; payer: string | null; has_photo: number;
}
export interface EventExpense {
  expense_id: number; kydemy_id: number | null; datetime: string | null;
  amount: string; category: string | null; notes: string | null;
}
export interface EventFinance {
  income: { total: number; count: number; list: EventIncome[] };
  expenses: { total: number; count: number; list: EventExpense[] };
  profit: number;
}
export function getEventFinance(eventId: number) {
  return api.get<EventFinance>(`/events/${eventId}/finance`);
}
export function addEventExpense(eventId: number, e: { amount: number; category: string; notes?: string; date: string }) {
  return api.post<{ ok: true; expense_id: number }>(`/events/${eventId}/expenses`, e);
}
export function deleteEventExpense(eventId: number, expenseId: number) {
  return api.del<{ ok: true }>(`/events/${eventId}/expenses/${expenseId}`);
}

// ── Finanzas del festival (Mabel 26-oct-2026): presupuesto, libreta, resumen, comparativo ──
export type FestKind = 'ingreso' | 'gasto';
export interface FestRenglon {
  id: number; event_id: number; kind: FestKind; tipo: 'boletos' | 'otro'; concept: string;
  qty: number | null; unit_price: number | null; amount: number; note: string | null; sort: number;
  real?: number; diferencia?: number;
}
export interface FestMov {
  origen: 'evento' | 'caja' | 'gastos'; id: number; kind: FestKind; budget_id: number | null; concepto: string; fecha: string;
  qty: number | null; amount: number; method: string | null; note: string | null; editable: boolean; tipo?: string;
}
export interface FestResumen {
  evento: { event_id: number; name: string; datetime: string | null; fecha: string | null; price: string | null; fin_mode: 'completo' | 'utilidad';
    is_reference: boolean; fin_concept_id: number | null; profit_payment_id: number | null };
  presupuesto: { renglones: FestRenglon[]; ingresos: number; gastos: number; utilidad: number; boletos: { meta: number | null; precio: number | null };
    equilibrio: number | null; otrosIngresos: number };
  real: { movimientos: FestMov[]; sinRenglon: FestMov[]; ingresos: number; gastos: number; utilidad: number; boletos: { vendidos: number } };
  finanzas: null | { modo: 'completo' } | { modo: 'utilidad'; concepto: { concept_id: number; name: string } | null; enCaja: number; aPasar: number;
    pagado: { payment_id: number; fecha: string; amount: number } | null; puedePasar: boolean; motivo: string | null };
}
export interface FestColumna {
  event_id: number; nombre: string; fecha: string | null; referencia: boolean; cifras: string; boletos: number | null; precio: number | null;
  ingresos: number; gastos: number; utilidad: number; otrosIngresos: number; margen: number | null; costoPorBoleto: number | null; equilibrio: number | null;
}
export interface FestComparativo { columnas: FestColumna[]; gastos: { concepto: string; montos: Record<string, number> }[] }
export interface FestRenglonInput { kind: FestKind; tipo?: 'boletos' | 'otro'; concept: string; qty?: number | string | null; unit_price?: number | string | null; amount?: number | string | null; note?: string | null }
export interface FestMovInput { kind: FestKind; concept: string; budget_id?: number | null; fecha: string; qty?: number | string | null; amount: number | string; method?: string | null; note?: string | null }

export function getFestival(eventId: number) { return api.get<FestResumen>(`/events/${eventId}/festival`); }
export function getComparativo() { return api.get<FestComparativo>('/events/comparativo'); }
export function setFestivalConfig(eventId: number, c: { fin_mode?: string; is_reference?: boolean; fin_concept_id?: number | null }) {
  return api.put<{ ok: true }>(`/events/${eventId}/festival/config`, c);
}
export function addRenglon(eventId: number, r: FestRenglonInput) { return api.post<{ ok: true; id: number }>(`/events/${eventId}/budget`, r); }
export function updateRenglon(eventId: number, id: number, r: FestRenglonInput) { return api.put<{ ok: true }>(`/events/${eventId}/budget/${id}`, r); }
export function deleteRenglon(eventId: number, id: number) { return api.del<{ ok: true }>(`/events/${eventId}/budget/${id}`); }
export function copyPresupuesto(eventId: number, fromEventId: number) { return api.post<{ ok: true; copiados: number }>(`/events/${eventId}/budget/copy`, { from_event_id: fromEventId }); }
export function addMovimiento(eventId: number, m: FestMovInput) { return api.post<{ ok: true; id: number }>(`/events/${eventId}/movements`, m); }
export function updateMovimiento(eventId: number, id: number, m: FestMovInput) { return api.put<{ ok: true }>(`/events/${eventId}/movements/${id}`, m); }
export function deleteMovimiento(eventId: number, id: number) { return api.del<{ ok: true }>(`/events/${eventId}/movements/${id}`); }
export function pasarUtilidad(eventId: number) { return api.post<{ ok: true; payment_id: number; amount: number }>(`/events/${eventId}/utilidad`, {}); }
export function quitarUtilidad(eventId: number) { return api.del<{ ok: true }>(`/events/${eventId}/utilidad`); }
export function urlFestivalXlsx(eventId: number) { return `${DATA_BASE}/events/${eventId}/festival.xlsx`; }

// ── Control de boletos del festival (Mabel 28-oct-2026; backend lib/letsmoveEventoBoletos.js) ──
// Forma EXACTA de `tablero()`: lista de espera y libres vienen CALCULADOS por el backend, aquí solo se pintan.
export type BolMetodo = 'cash' | 'wire' | 'creditcard';
export const BOL_METODO: Record<BolMetodo, string> = { cash: 'Efectivo', wire: 'Transferencia', creditcard: 'Tarjeta' };
export interface BolFase {
  phase_id: number; name: string; starts_at: string | null; ends_at: string | null;
  capacity: number | null; max_per_account: number | null; price: number | null; sort: number; active: boolean;
  solo_participantes: boolean; solo_lista_espera: boolean; sold: number;
  abierta: boolean; solicitado: number; espera: number; participantes_que_compraron: number; participantes_sin_comprar: number;
  disponibles: number | null;
}
export interface BolCifras { solicitado: number; vendido: number; espera: number; libres: number | null; entregado: number; monto: number; compras: number }
export interface BolTicket {
  id: number; phase_id: number | null; contact_id: number | null; buyer_name: string | null; buyer_phone: string | null; folio: string | null;
  // Karla 28-oct: número de participación (familia) y sufijo en el folio; cortesías y compras del equipo (maestros/staff)
  folio_base?: string | null; staff_user_id?: number | null; cortesia?: boolean; equipo?: boolean; staff_name?: string | null;
  qty_requested: number; qty_sold: number; qty_delivered: number; unit_price: number | null; amount: number; method: BolMetodo | null;
  paid_at: string | null; attended_by: number | null; note: string | null; movement_id: number | null;
  cancelled: boolean; cancelled_at: string | null; created_at: string | null;
  first_name: string | null; last_name: string | null; contact_phone: string | null; phase_name: string | null; attended_name: string | null;
  comprador: string; telefono: string | null;
}
export interface BolComprador {
  contact_id: number | null; nombre: string; first_name: string | null; last_name: string | null; telefono: string | null;
  por_fase: Record<string, BolCifras>; totales: BolCifras; tickets: BolTicket[];
  participante: boolean; participant_id?: number; active?: boolean; class_label?: string | null; clase_real?: string | null;
  family_label?: string | null; folio_base?: string | null; cortesias?: number; staff_user_id?: number | null; equipo?: boolean;
}
/** Un renglón del sorteo de asientos: un número de participación por familia (o comprador); el equipo no entra. */
export interface BolSorteo { folio: string; nombres: string[]; familia: string | null; boletos: number; compras: number; folios: string[] }
/** Maestro o staff con su cortesía (1 por persona) y compras en el evento. */
export interface BolEquipo { user_id: number; nombre: string; es_profesor: boolean; puesto: string; cortesia_ticket_id: number | null; cortesia_folio: string | null; comprados: number }
export interface BolParticipante {
  id: number; contact_id: number; class_label: string | null; family_label?: string | null; source: 'excel' | 'manual'; active: boolean; created_at: string | null;
  first_name: string | null; last_name: string | null; phone: string | null; clase_real: string | null; nombre: string;
}
export interface BolTurno {
  id: number; opened_at: string; open_amount: number; closed_at: string | null; close_amount: number | null;
  expected_amount: number | null; difference: number | null; notes: string | null; opened_by_name: string | null; closed_by_name: string | null;
  // Solo en la caja abierta: efectivo de boletos cobrado en el turno y esperado en vivo.
  efectivo?: number; cobros?: number; esperado_ahora?: number | null;
}
export interface BolCaja { abierta: BolTurno | null; historial: BolTurno[] }
export interface BolMarcador {
  cupo: number | null; vendidos: number; disponibles: number | null; en_espera: number; entregados: number;
  recaudado: { total: number; cash: number; wire: number; creditcard: number };
  compras: number; solicitudes: number; participantes: number; participantes_que_compraron: number;
  cortesias?: number; folios_sorteo?: number;
}
export interface BolTablero {
  evento: { event_id: number; name: string; datetime: string | null; price: number | null; capacity: number | null; ticket_mode: 'simple' | 'festival'; fin_mode: string; venue: string | null };
  fase_activa: number | null; fases: BolFase[]; marcador: BolMarcador;
  participantes: BolComprador[]; externos: BolComprador[]; equipo?: BolComprador[]; sorteo?: BolSorteo[]; inactivos: BolParticipante[]; tickets: BolTicket[]; caja: BolCaja;
}
export interface BolVentaInput {
  phase_id?: number | null; forzar?: boolean; contact_id?: number | null; buyer_name?: string | null; buyer_phone?: string | null;
  staff_user_id?: number | null; cortesia?: boolean;
  qty_requested: number; qty_sold: number; unit_price?: number | string | null; method?: BolMetodo | null; paid_at?: string | null; note?: string | null;
}
export interface BolVentaResp { ok: true; id: number; folio: string | null; folio_base?: string | null; cortesia?: boolean; movement_id: number | null; phase_id: number | null; fase: string | null; comprador: string; qty_requested: number; qty_sold: number; unit_price: number; amount: number; method: BolMetodo | null; paid_at: string; note: string | null; espera: number; quedan_tope: number | null }
export interface BolAdicionales {
  phase_id: number; fase: string; cupo: number | null; vendidos: number; disponibles: number | null;
  participantes_con_espera: number; espera_total: number; sugerido: number | null; alcanza_para_todos: boolean | null;
  detalle: { contact_id: number | null; nombre: string; espera: number }[];
}
export interface BolRecibo {
  data: BolTicket & { metodo: string | null; espera?: number; sufijo?: string | null; familia?: string[]; family_label?: string | null; participa_sorteo?: boolean };
  evento: { event_id: number; name: string; datetime: string | null; venue: string | null; venue_address: string | null };
  studio: { name: string; address: string; prefix: string };
}

export function getBoletaje(eventId: number) { return api.get<BolTablero>(`/events/${eventId}/boletaje`); }
export function setBoletajeConfig(eventId: number, c: { ticket_mode?: 'simple' | 'festival'; capacity?: number | string | null }) {
  return api.put<{ ok: true; ticket_mode: string; capacity: number | null }>(`/events/${eventId}/boletaje/config`, c);
}
export function urlBoletajeXlsx(eventId: number) { return `${DATA_BASE}/events/${eventId}/boletaje.xlsx`; }
// Participantes (quién tiene prioridad). Buscar alumnos: `listMembers` de members/api.
export function listParticipantes(eventId: number) { return api.get<{ data: BolParticipante[] }>(`/events/${eventId}/participants`); }
export function addParticipante(eventId: number, p: { contact_id: number; class_label?: string | null }) {
  return api.post<{ ok: true; id: number; reactivado?: boolean }>(`/events/${eventId}/participants`, p);
}
export function addParticipantesLote(eventId: number, items: { contact_id: number; class_label?: string | null }[]) {
  return api.post<{ ok: true; insertados: number; repetidos: number; inexistentes: number }>(`/events/${eventId}/participants/bulk`, { items });
}
/** Maestros y staff del estudio con su cortesía y compras en el evento (Karla 28-oct). */
export function getEquipoBoletaje(eventId: number) { return api.get<{ data: BolEquipo[] }>(`/events/${eventId}/boletaje/equipo`); }
/** Etiqueta como familia a los participantes que comparten teléfono (reversible: se edita por participante). */
export function agruparFamiliasPorTelefono(eventId: number) { return api.post<{ ok: true; familias: number; etiquetados: number }>(`/events/${eventId}/participants/familias-por-telefono`, {}); }
export function patchParticipante(eventId: number, pid: number, p: { active?: boolean; class_label?: string | null; family_label?: string | null }) {
  return api.patch<{ ok: true }>(`/events/${eventId}/participants/${pid}`, p);
}
export function removeParticipante(eventId: number, pid: number) { return api.del<{ ok: true }>(`/events/${eventId}/participants/${pid}`); }
// Ventas (una fila por compra; folio FEST-00001 y renglón «Boletos» en la libreta los pone el backend)
export function venderBoletos(eventId: number, v: BolVentaInput) { return api.post<BolVentaResp>(`/events/${eventId}/tickets`, v); }
export function editarVenta(eventId: number, tid: number, v: Partial<BolVentaInput>) { return api.put<BolVentaResp>(`/events/${eventId}/tickets/${tid}`, v); }
export function entregarBoletos(eventId: number, tid: number, qty_delivered: number) {
  return api.patch<{ ok: true; qty_delivered: number; pendientes: number }>(`/events/${eventId}/tickets/${tid}/entregar`, { qty_delivered });
}
export function anularVenta(eventId: number, tid: number) { return api.del<{ ok: true }>(`/events/${eventId}/tickets/${tid}`); }
export function getRecibo(eventId: number, tid: number) { return api.get<BolRecibo>(`/events/${eventId}/tickets/${tid}`); }
// Cerrar fase: cuántos adicionales por participante (solo propone, no escribe)
export function getAdicionales(eventId: number, phaseId: number) { return api.get<BolAdicionales>(`/events/${eventId}/phases/${phaseId}/adicionales`); }
// Caja del festival (aparte de la Caja de operación)
export function getCajaFestival(eventId: number) { return api.get<BolCaja>(`/events/${eventId}/cash`); }
export function abrirCajaFestival(eventId: number, b: { open_amount: number; notes?: string | null }) {
  return api.post<{ ok: true; id: number; open_amount: number }>(`/events/${eventId}/cash/open`, b);
}
export function cerrarCajaFestival(eventId: number, b: { close_amount: number; notes?: string | null }) {
  return api.post<{ ok: true; id: number; open_amount: number; efectivo: number; cobros: number; expected_amount: number; close_amount: number; difference: number }>(`/events/${eventId}/cash/close`, b);
}

// ── Galería del evento (fotos / videos) ──
export interface EventMedia {
  media_id: number; kind: 'photo' | 'video'; original_name: string;
  mime: string | null; size: number | null; caption: string | null;
  created_at: string | null; uploaded_by_name: string | null;
}
export function listEventMedia(eventId: number) {
  return api.get<{ data: EventMedia[]; count: number }>(`/events/${eventId}/media`);
}
export async function uploadEventMedia(eventId: number, file: File, caption?: string) {
  const headers: Record<string, string> = {
    'Content-Type': file.type || 'application/octet-stream',
    'X-File-Name': encodeURIComponent(file.name),
  };
  if (caption) headers['X-Caption'] = encodeURIComponent(caption);
  const res = await fetch(`${DATA_BASE}/events/${eventId}/media`, { method: 'POST', credentials: 'include', headers, body: file });
  if (!res.ok) { const t = await res.json().catch(() => null); throw new Error((t && t.error) || `HTTP ${res.status}`); }
  return res.json() as Promise<{ ok: true; media_id: number; kind: string }>;
}
export function deleteEventMedia(eventId: number, mediaId: number) {
  return api.del<{ ok: true }>(`/events/${eventId}/media/${mediaId}`);
}
export function eventMediaUrl(eventId: number, mediaId: number) {
  return `${DATA_BASE}/events/${eventId}/media/${mediaId}/file`;
}

// ── Portada del evento (28-oct-2026) ──
// Bytes crudos como la galería; el servidor la optimiza (WebP, lado ≤1600 px) y devuelve la URL relativa
// `/event-covers/<archivo>.webp` que queda en `image_url`. Tope 15 MB; JPG, PNG, WebP (HEIC no siempre se lee).
export const COVER_MAX_MB = 15;
export async function uploadEventCover(eventId: number, file: File) {
  const headers: Record<string, string> = {
    'Content-Type': file.type || 'application/octet-stream',
    'X-File-Name': encodeURIComponent(file.name),
  };
  const res = await fetch(`${DATA_BASE}/events/${eventId}/cover`, { method: 'POST', credentials: 'include', headers, body: file });
  if (!res.ok) { const t = await res.json().catch(() => null); throw new Error((t && t.error) || `HTTP ${res.status}`); }
  return res.json() as Promise<{ ok: true; image_url: string; width: number | null; height: number | null; bytes: number }>;
}
export function deleteEventCover(eventId: number) {
  return api.del<{ ok: true }>(`/events/${eventId}/cover`);
}

export function money(n: number | string) {
  return '$' + Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 });
}
/** Formatea un datetime "YYYY-MM-DDTHH:mm:ss" como hora local (ya viene en hora Mexicali). */
export function fmtEventDate(dt: string | null, withTime = true): string {
  if (!dt) return 'Fecha por definir';
  const m = dt.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!m) return dt;
  const [, y, mo, d, h, mi] = m.map(Number) as unknown as number[];
  const date = new Date(y, mo - 1, d, h, mi);
  const opts: Intl.DateTimeFormatOptions = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
  let s = date.toLocaleDateString('es-MX', opts);
  // 00:00 = solo se sabe el día (p. ej. un festival pasado capturado de una hoja): no se inventa «12:00 a.m.».
  if (withTime && (h || mi)) s += ` · ${date.toLocaleTimeString('es-MX', { hour: 'numeric', minute: '2-digit' })}`;
  return s;
}
